import React, { useCallback } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { ClassNameMap } from '@mui/styles';
import {
  AutocompleteRenderOptionState,
  AutocompleteRenderInputParams,
  AutocompleteInputChangeReason
} from '@mui/material/Autocomplete';
import { FormattedMessage } from 'react-intl';
import Autocomplete from '@mui/material/Autocomplete';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import AutocompleteInput from './AutocompleteInput';
const useAutocompleteStyles: (props: {
  hasMore?: boolean | undefined;
  classes?: ClassNameMap;
}) => ClassNameMap<
  'tag' | 'listbox' | 'loading' | 'noOptions' | 'option'
> = makeStyles((theme) => ({
  tag: {
    background: theme.palette.grey[400]
  },
  loading: {
    fontSize: theme.typography.body2.fontSize
  },
  noOptions: {
    fontSize: theme.typography.body2.fontSize
  },
  option: {
    fontSize: theme.typography.body2.fontSize
  },
  listbox: {
    '& li:last-child': {
      opacity: ({ hasMore }: { hasMore?: boolean }) => (hasMore ? 1 : undefined)
    }
  }
}));

type SearchAutocompleteProps<T> = {
  dataTestId: string;
  loading: boolean;
  options: Array<T>;
  classes?: ClassNameMap;
  value: T[];
  hasError?: boolean;
  helperText?: React.ReactNode | string;
  alwaysShowHelperText?: boolean;
  variant?: 'filled' | 'standard' | 'outlined';
  hiddenLabel?: boolean;
  hasMore?: boolean;

  onChange: (value: T) => void;
  getOptionDisabled?: (option: T) => boolean;
  getOptionSelected: (option: T, value: T) => boolean;
  getOptionLabel: (option: T) => string;
  renderOption?: (
    _: any,
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  noOptionsText?: React.ReactNode | string;
  onInputChange?: (
    event: React.ChangeEvent<any>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  onClick?: () => void;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  disabled?: boolean;
};

const loadingMessage = <FormattedMessage id="search.facets.loading" />;

export const SearchAutocomplete = <T,>({
  dataTestId,
  value,
  onChange,
  loading = false,
  options,
  noOptionsText,
  renderOption,
  hasError = false,
  helperText,
  alwaysShowHelperText = false,
  getOptionSelected,
  getOptionDisabled,
  getOptionLabel,
  hiddenLabel = false,
  onInputChange,
  classes: classesOverride,
  onClick,
  filterOptions,
  hasMore = false,
  disabled
}: SearchAutocompleteProps<T>): React.ReactElement => {
  const classes = useAutocompleteStyles({ classes: classesOverride, hasMore });
  const input = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <AutocompleteInput
        showError={hasError}
        errorMessage={helperText}
        helperText={helperText}
        hiddenLabel={hiddenLabel}
        alwaysShowHelperText={alwaysShowHelperText}
        onClick={onClick}
        variant="standard"
        {...params}
      />
    ),
    [alwaysShowHelperText, helperText, hasError, hiddenLabel, onClick]
  );

  const handleChange = useCallback(
    (_, values) => {
      onChange(values);
    },
    [onChange]
  );
  return (
    <Autocomplete<T, boolean>
      data-testid={dataTestId}
      classes={classes}
      loading={loading}
      options={options}
      size={'small'}
      multiple
      fullWidth
      renderOption={renderOption}
      loadingText={loadingMessage}
      value={value}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      renderInput={input}
      noOptionsText={noOptionsText}
      isOptionEqualToValue={getOptionSelected}
      getOptionDisabled={getOptionDisabled}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      disabled={disabled}
    />
  );
};

export default SearchAutocomplete;
